import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plane, TrendingUp, Lightbulb, Radar, Target, Megaphone, Users, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { frNum, fmtCompact, scoreLabel, scoreClasses } from "@/lib/marketDisplay";
import { isoFromCountry, localizeCountry } from "@/lib/marketFormat";
import { LodgeAiDrawer } from "@/components/LodgeAiDrawer";
import { isMauritiusDestination } from "@/lib/mauritiusDemoJourney";
import { demoMauritiusMarketDetail } from "@/lib/demoMauritius";
import { marketMonthLabels } from "@/lib/dashboardContext";

const NA = ({ msg }) => <p className="text-sm italic text-slate-400">{msg || "Non disponible"}</p>;

const Kpi = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-1 font-heading text-xl font-bold text-[#1E3D2B]">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);

const Card = ({ title, icon: Icon, children, testid, source }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="mb-4 flex items-center justify-between">
      <h2 className="flex items-center gap-2 font-heading text-base font-bold text-[#17201A]">
        {Icon && <Icon size={16} className="text-[#3E7256]" />} {title}
      </h2>
      {source && <span className="rounded-full bg-[#ECF3EC] px-2 py-0.5 text-[10px] font-semibold text-[#2E7D46]">{source}</span>}
    </div>
    {children}
  </div>
);

const MonthlyBars = ({ data, periodStart }) => {
  if (!data || !data.some((v) => v)) return <NA msg="Les données touristiques mensuelles ne sont pas disponibles pour ce marché." />;
  const max = Math.max(...data) || 1;
  const monthLabels = marketMonthLabels(periodStart, data.length);
  return (
    <div className="flex items-end gap-1.5" data-testid="tourism-monthly">
      {data.map((v, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1">
          <div className="w-full rounded-t bg-[#3E7256]/80" style={{ height: `${Math.max(2, (v / max) * 96)}px` }} title={`${monthLabels[i]} : ${frNum(v)}`} />
          <span className="text-[9px] text-slate-400">{monthLabels[i]}</span>
        </div>
      ))}
    </div>
  );
};

const marketOrigin = (key = "") => String(key).split("-")[0].toUpperCase();

export const resolveCanonicalMarketKey = (requestedKey, markets = [], destination) => {
  const requested = String(requestedKey || "").toUpperCase();
  const exact = markets.find((market) => String(market?.key || "").toUpperCase() === requested);
  if (exact) return exact.key;

  const candidates = markets.filter((market) => marketOrigin(market?.key) === marketOrigin(requested));
  if (candidates.length === 1) return candidates[0].key;

  const destinationIso = isoFromCountry(destination);
  const destinationMatch = candidates.find((market) =>
    destinationIso && isoFromCountry(market?.destination) === destinationIso);
  return destinationMatch?.key || requestedKey;
};

const sharedAirDetail = (payload) => {
  if (!payload?.kpis) return null;
  return {
    market_key: payload.market?.key,
    origin: payload.market?.origin,
    destination: payload.market?.destination,
    passengers: payload.kpis.airPassengers?.raw,
    revenue: payload.kpis.airRevenue?.raw,
    avg_fare: payload.kpis.averageFare?.raw,
    top_airlines: (payload.topAirlinesByPassengers || []).map((airline) => ({
      rank: airline.rank,
      name: airline.name,
      passengers: airline.raw,
    })),
  };
};

export default function MarketIntelligence() {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const { activeOrgId, activeMembership } = useAuth();
  const [d, setD] = useState(null);
  const [loadedScope, setLoadedScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const org = activeMembership?.org || {};
  const immutableDemo = org.is_demo && org.demo_mode === "immutable" && isMauritiusDestination(org);
  const destCountry = org.country || "votre destination";

  useEffect(() => {
    if (!activeOrgId) return;
    let cancelled = false;

    const loadMarket = async () => {
      setLoading(true);
      setNotFound(false);
      setD(null);

      const catalog = await api.get("/market-data/markets", { headers: { "X-Organization-ID": activeOrgId } })
        .then(({ data }) => data?.markets || [])
        .catch(() => []);
      if (cancelled) return;
      const canonicalKey = resolveCanonicalMarketKey(marketId, catalog, org.country);
      const originKey = marketOrigin(canonicalKey || marketId);
      const sharedMarket = catalog.find((market) => market.key === canonicalKey);

      if (canonicalKey && canonicalKey !== marketId) {
        navigate(`/markets/${canonicalKey}`, { replace: true });
      }

      let detail = null;
      for (const key of [...new Set([canonicalKey, originKey, marketId].filter(Boolean))]) {
        try {
          detail = (await api.get(`/organizations/${activeOrgId}/markets/${key}`)).data;
          break;
        } catch (error) {
          if (error.response?.status !== 404) break;
        }
      }

      const sharedAir = canonicalKey
        ? await api.get(`/market-data/air/${canonicalKey}`, { headers: { "X-Organization-ID": activeOrgId } }).then(({ data }) => data).catch(() => null)
        : null;
      // Demo Maurice: figure-free sheet for the 4 approved routes only; real shared ADI air is used below.
      const fallback = !detail && immutableDemo ? demoMauritiusMarketDetail(canonicalKey || marketId) : null;
      const base = detail || fallback;
      const air = base?.air || sharedAirDetail(sharedAir);

      if (!base && !air) {
        if (!cancelled) setNotFound(true);
        return;
      }

      const market = {
        ...(base?.market || {}),
        key: base?.market?.key || originKey,
        iso: base?.market?.iso || originKey,
        name: base?.market?.name || sharedMarket?.origin || originKey,
        air_market_key: canonicalKey || base?.market?.air_market_key,
        air_passengers: base?.market?.air_passengers ?? air?.passengers ?? null,
      };
      if (!cancelled) setD({ ...(base || {}), market, air });
    };

    loadMarket().finally(() => {
      if (!cancelled) {
        setLoadedScope(`${activeOrgId}:${marketId}`);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [activeOrgId, immutableDemo, marketId, navigate, org.country]);

  if (loading || loadedScope !== `${activeOrgId}:${marketId}`) return <div className="py-20 text-center text-sm text-slate-400" data-testid="market-detail-loading">Chargement…</div>;
  if (notFound || !d) return (
    <div className="py-20 text-center" data-testid="market-detail-notfound">
      <p className="text-sm text-slate-500">Marché introuvable.</p>
      <Link to="/markets" className="mt-3 inline-block text-sm font-semibold text-[#3E7256] hover:underline">← Retour à Markets</Link>
    </div>
  );

  const m = d.market;
  const marketDisplayName = localizeCountry(m.name);
  const destinationDisplayName = localizeCountry(destCountry);
  const localizeMarketText = (text) => String(text || "").replaceAll(m.name, marketDisplayName);
  const tourismSource = (m.sources?.volume === "LODGE" || m.sources?.seasonality === "LODGE" || m.sources?.spend === "LODGE")
    ? "Lodge Data" : (m.volume != null || m.seasonality ? "Client Data" : null);
  const goCampaign = () => navigate("/campaigns", { state: { prefill: { market: marketDisplayName, objective: "", period: "", kpis: [] } } });

  return (
    <div className="space-y-6" data-testid="market-intelligence">
      {d.data_unavailable && (
        <div data-testid="market-detail-unavailable-notice" className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
          <strong>Démonstration Maurice.</strong> Seules les données aériennes Lodge Market Data sont disponibles pour ce marché ; les indicateurs touristiques sont affichés « non disponibles ».
        </div>
      )}
      {d.simulation && (
        <div data-testid="market-detail-demo-notice" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
          <strong>Simulation illustrative.</strong> Cette fiche permet de parcourir la démonstration ; elle ne constitue pas une prédiction ni une donnée client.
        </div>
      )}
      <button data-testid="back-to-markets" onClick={() => navigate("/markets")} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#1E3D2B]">
        <ArrowLeft size={15} /> Retour à Markets
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="market-header">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Market Intelligence</p>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-heading text-2xl font-bold text-[#1E3D2B] sm:text-3xl">{marketDisplayName} <span className="text-slate-300">→</span> {destinationDisplayName}</h1>
          <div className="text-right">
            {m.lodge_score == null ? (
              <span className="text-sm italic text-slate-400">Opportunity Score non disponible</span>
            ) : (
              <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-bold ${scoreClasses(m.lodge_score)}`} data-testid="detail-score">
                {m.lodge_score} / 100 <span className="font-semibold opacity-70">· {scoreLabel(m.lodge_score)}</span>
              </span>
            )}
          </div>
        </div>
        {d.recommendation && <p className="mt-3 max-w-2xl text-sm text-slate-600">{localizeMarketText(d.recommendation)}</p>}
        {m.lodge_score == null && <p className="mt-3 text-sm text-slate-500">Opportunity Score disponible lorsque suffisamment de données auront été collectées.</p>}
      </div>

      {/* KPIs (max 5) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="market-kpis">
        <Kpi testid="kpi-visitors" label="Visiteurs / Nuitées" value={m.volume == null ? "—" : fmtCompact(m.volume)} sub={m.evolution == null ? null : `${m.evolution >= 0 ? "+" : ""}${m.evolution} %`} />
        <Kpi testid="kpi-air" label="Passagers aériens" value={m.air_passengers == null ? "—" : fmtCompact(m.air_passengers)} sub={m.air_passengers == null ? "Non disponible" : "Lodge Data"} />
        <Kpi testid="kpi-share" label="Part de marché" value={m.part_destination == null ? "—" : `${m.part_destination} %`} />
        <Kpi testid="kpi-spend" label="Dépense" value={m.spend == null ? "—" : fmtCompact(m.spend)} />
        <Kpi testid="kpi-los" label="Durée moyenne" value={m.length_of_stay == null ? "—" : `${m.length_of_stay} j`} sub={m.length_of_stay == null ? "Non disponible" : null} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tourism Intelligence */}
        <Card title="Tourism Intelligence" icon={TrendingUp} testid="tourism-intelligence" source={tourismSource}>
          <p className="mb-3 text-xs text-slate-500">Saisonnalité de la demande touristique (visiteurs / nuitées par mois).</p>
          <MonthlyBars data={m.seasonality} periodStart={m.period_start} />
        </Card>

        {/* Air Intelligence */}
        <Card title="Air Intelligence" icon={Plane} testid="air-intelligence" source={d.air ? "Lodge Data" : null}>
          {!d.air ? (
            <NA msg="Les données aériennes ne sont pas disponibles pour ce marché (aucun flux entrant vers votre destination)." />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Kpi label="Passagers" value={fmtCompact(d.air.passengers)} />
                <Kpi label="Revenus" value={fmtCompact(d.air.revenue)} />
                <Kpi label="Tarif moyen" value={`${frNum(d.air.avg_fare)} $`} />
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold text-slate-500">Top compagnies — Passagers</p>
                <ul className="space-y-1.5" data-testid="air-top-airlines">
                  {d.air.top_airlines.map((a) => (
                    <li key={a.rank} className="flex justify-between text-sm">
                      <span className="truncate text-slate-700">{a.rank}. {a.name}</span>
                      <span className="ml-3 shrink-0 font-mono text-xs text-[#1E3D2B]">{fmtCompact(a.passengers)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Card>

        {/* Lodge Intelligence */}
        <Card title="Lodge Intelligence" icon={Lightbulb} testid="lodge-intelligence">
          {(d.observations?.length || 0) === 0 && !d.recommendation ? (
            <NA msg="Analyse indisponible : données insuffisantes pour ce marché." />
          ) : (
            <>
              {d.observations?.length > 0 && (
                <>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Pourquoi ce marché est intéressant</p>
                  <ul className="mb-4 space-y-1.5 text-sm text-slate-600">
                    {d.observations.map((o, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#3E7256]" />{localizeMarketText(o)}</li>)}
                  </ul>
                </>
              )}
              {d.recommendation && (
                <div className="rounded-lg bg-[#F5F8F5] px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#3E7256]">Recommandation Lodge</p>
                  <p className="mt-1 text-sm text-slate-700">{localizeMarketText(d.recommendation)}</p>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Signals & Watch */}
        <Card title="Signals & Watch" icon={Radar} testid="signals-watch">
          {(d.signals?.length || 0) === 0 ? (
            <NA msg="Aucun signal détecté pour l'instant. Les signaux apparaîtront lorsque les données touristiques et aériennes se croiseront." />
          ) : (
            <ul className="space-y-2" data-testid="signals-list">
              {d.signals.map((s, i) => (
                <li key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-sm font-semibold text-[#1E3D2B]">{s.type}</p>
                  <p className="text-xs text-slate-500">{s.detail}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Quick actions */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="quick-actions">
        <h2 className="mb-3 font-heading text-base font-bold text-[#17201A]">Passer à l'action</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/opportunities" data-testid="action-opportunities" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <Target size={15} /> Voir les opportunités
          </Link>
          <button onClick={goCampaign} data-testid="action-campaign" className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540]">
            <Megaphone size={15} /> Créer une campagne
          </button>
          <Link to="/partners" data-testid="action-partners" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            <Users size={15} /> Trouver des partenaires
          </Link>
          <button onClick={() => setAiOpen(true)} data-testid="action-analyze-market-ai" className="inline-flex items-center gap-2 rounded-lg border border-[#BBD8C5] bg-[#F1F7F3] px-4 py-2.5 text-sm font-semibold text-[#2C5540] hover:bg-[#E4F0E9]">
            <Sparkles size={15} /> Analyser avec Lodge AI
          </button>
        </div>
      </div>

      <LodgeAiDrawer open={aiOpen} onClose={() => setAiOpen(false)} orgId={activeOrgId}
        actionType="analyze_market" context={{ marketKey: m.key, market: m.name }} title={`Analyse — ${m.name}`}
        demoSimulation={immutableDemo} demoVariant={immutableDemo ? "mauritius" : undefined} />
    </div>
  );
}
